import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Download, LogOut, RefreshCw, Search, Mail, MessageCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cb_admin_token";

const CAT_LABEL = {
  cash_flow: "Cash Flow",
  profitability: "Profitability",
  compliance: "Compliance",
  controls: "Controls",
  growth: "Growth",
};

export default function AdminLeads() {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY) || ""; } catch (_) { return ""; }
  });
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("leads");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const headers = token ? { "X-Admin-Token": token } : {};

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      setToken(data.token);
      try { localStorage.setItem(TOKEN_KEY, data.token); } catch (_) { /* ignore */ }
      toast.success("Logged in");
    } catch {
      toast.error("Invalid password");
    } finally { setLoading(false); }
  };

  const logout = () => {
    setToken(""); setPassword("");
    try { localStorage.removeItem(TOKEN_KEY); } catch (_) { /* ignore */ }
  };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
      ]);
      setLeads(a.data.leads || []);
      setContacts(b.data.contacts || []);
    } catch (e) {
      if (e.response?.status === 401) { logout(); toast.error("Session expired"); }
      else toast.error("Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = leads.filter((l) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (l.name || "").toLowerCase().includes(s) ||
      (l.email || "").toLowerCase().includes(s) ||
      (l.company || "").toLowerCase().includes(s) ||
      (l.industry || "").toLowerCase().includes(s)
    );
  });

  const exportCsv = () => {
    const url = `${API}/admin/leads.csv?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-cb-cream grid place-items-center px-6">
        <form onSubmit={login} className="w-full max-w-sm card-cb p-8" data-testid="admin-login-form">
          <div className="w-12 h-12 rounded-xl bg-cb-dark text-white grid place-items-center"><Lock size={22} /></div>
          <h1 className="mt-5 text-2xl font-semibold text-cb-primary" style={{ fontFamily: "Outfit" }}>Capital Bay Admin</h1>
          <p className="mt-1 text-sm text-cb-secondary">Sign in to view leads and contact messages.</p>
          <Label className="mt-6 block">Password</Label>
          <Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="admin-password" className="mt-1" />
          <button type="submit" disabled={loading} className="mt-6 btn-primary w-full py-3 rounded-full font-medium disabled:opacity-50" data-testid="admin-login-submit">
            {loading ? "..." : "Sign in"}
          </button>
          <a href="/" className="mt-4 block text-center text-xs text-cb-secondary hover:text-cb-primary">← Back to site</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cb-cream">
      <header className="bg-cb-dark text-white">
        <div className="container-cb flex items-center justify-between py-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-cb-gold grid place-items-center text-cb-primary">
              <span style={{ fontFamily: "Outfit", fontWeight: 700 }}>CB</span>
            </div>
            <div>
              <div className="font-semibold" style={{ fontFamily: "Outfit" }}>Capital Bay · Admin</div>
              <div className="text-[11px] tracking-widest uppercase text-white/60">Leads & Inbox</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="px-3 py-2 rounded-full border border-white/20 hover:bg-white/10 text-sm inline-flex items-center gap-1" data-testid="admin-refresh"><RefreshCw size={14} /> Refresh</button>
            <button onClick={logout} className="px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm inline-flex items-center gap-1" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="container-cb py-10">
        <div className="grid sm:grid-cols-4 gap-4">
          <Stat label="Total leads" value={leads.length} />
          <Stat label="Average score" value={leads.length ? Math.round(leads.reduce((s, l) => s + (l.score || 0), 0) / leads.length) : 0} suffix="/100" />
          <Stat label="A+ / A grade" value={leads.filter(l => ["A+", "A"].includes(l.grade)).length} />
          <Stat label="Contact messages" value={contacts.length} />
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full bg-cb-paper border border-cb-primary/10 p-1">
            <button onClick={()=>setTab("leads")} data-testid="tab-leads" className={`px-4 py-2 rounded-full text-sm ${tab === "leads" ? "bg-cb-dark text-white" : "text-cb-primary"}`}>Leads ({leads.length})</button>
            <button onClick={()=>setTab("contacts")} data-testid="tab-contacts" className={`px-4 py-2 rounded-full text-sm ${tab === "contacts" ? "bg-cb-dark text-white" : "text-cb-primary"}`}>Contacts ({contacts.length})</button>
          </div>
          {tab === "leads" && (
            <>
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-cb-secondary" />
                <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search name, email, company, industry…" className="ps-9" data-testid="admin-search" />
              </div>
              <button onClick={exportCsv} className="btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm" data-testid="admin-export-csv"><Download size={14} /> Export CSV</button>
            </>
          )}
        </div>

        {tab === "leads" ? (
          <div className="mt-6 overflow-x-auto card-cb">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-cb-accent-surface text-cb-secondary uppercase text-[11px] tracking-widest">
                <tr>
                  <th className="text-start p-3">Date</th>
                  <th className="text-start p-3">Name</th>
                  <th className="text-start p-3">Company</th>
                  <th className="text-start p-3">Industry</th>
                  <th className="text-end p-3">Score</th>
                  <th className="text-center p-3">Grade</th>
                  <th className="text-start p-3">Contact</th>
                  <th className="text-start p-3">Report</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-t border-cb-primary/5 hover:bg-cb-accent-surface/50" data-testid={`lead-row-${l.id}`}>
                    <td className="p-3 text-cb-secondary whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="p-3">
                      <div className="text-cb-primary font-medium">{l.name}</div>
                      <div className="text-xs text-cb-secondary">{l.role || "—"}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-cb-primary">{l.company}</div>
                      <div className="text-xs text-cb-secondary">{l.company_size || "—"}</div>
                    </td>
                    <td className="p-3 text-cb-primary capitalize">{l.industry?.replace("_", " ")}</td>
                    <td className="p-3 text-end font-semibold text-cb-primary">{l.score}</td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-cb-emerald text-white text-xs font-semibold">{l.grade}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <a href={`mailto:${l.email}`} title="Email" className="text-cb-emerald hover:text-cb-primary"><Mail size={14} /></a>
                        {l.phone && <a href={`https://wa.me/${l.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" title="WhatsApp" className="text-cb-emerald hover:text-cb-primary"><MessageCircle size={14} /></a>}
                      </div>
                      <div className="text-xs text-cb-secondary mt-1">{l.email}</div>
                    </td>
                    <td className="p-3">
                      {l.report_token ? (
                        <a href={`${API}/reports/${l.report_token}.pdf`} target="_blank" rel="noreferrer" className="text-xs text-cb-emerald inline-flex items-center gap-1 hover:underline" data-testid={`report-link-${l.id}`}><Download size={12} /> PDF</a>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="p-10 text-center text-cb-secondary">No leads yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="admin-contacts-list">
            {contacts.map((c) => (
              <div key={c.id} className="card-cb p-5" data-testid={`contact-row-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-cb-primary font-medium">{c.name}</div>
                  <div className="text-xs text-cb-secondary">{new Date(c.created_at).toLocaleString()}</div>
                </div>
                <div className="text-xs text-cb-secondary mt-1">{c.company || "—"}</div>
                <p className="mt-3 text-sm text-cb-primary leading-relaxed">{c.message}</p>
                <div className="mt-4 flex items-center gap-3 text-xs">
                  <a href={`mailto:${c.email}`} className="text-cb-emerald hover:underline inline-flex items-center gap-1"><Mail size={12} /> {c.email}</a>
                  {c.phone && <a href={`https://wa.me/${c.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-cb-emerald hover:underline inline-flex items-center gap-1"><MessageCircle size={12} /> {c.phone}</a>}
                </div>
              </div>
            ))}
            {contacts.length === 0 && <div className="p-10 text-center text-cb-secondary">No messages.</div>}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, suffix }) {
  return (
    <div className="card-cb p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs uppercase tracking-widest text-cb-secondary">{label}</div>
      <div className="mt-2 text-3xl font-semibold text-cb-primary" style={{ fontFamily: "Outfit" }}>
        {value}<span className="text-base text-cb-secondary ms-1">{suffix || ""}</span>
      </div>
    </div>
  );
}
